import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Close,
  Assessment,
  FileDownload,
  FilterList,
} from '@mui/icons-material';
import { PaymentLink, Payment } from '../../types/PaymentLink';
import { formatCurrency, formatDateTime } from '../../utils/validators';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

interface ReportsDialogProps {
  open: boolean;
  onClose: () => void;
  paymentLinks: PaymentLink[];
}

type DateRangeOption = 'today' | 'last7days' | 'last30days' | 'thisMonth' | 'lastMonth' | 'allTime' | 'custom';
type StatusFilter = 'all' | 'success' | 'partial' | 'pending';
type PaymentTypeFilter = 'all' | 'online' | 'manual';

const ReportsDialog: React.FC<ReportsDialogProps> = ({ open, onClose, paymentLinks }) => {
  const [dateRange, setDateRange] = useState<DateRangeOption>('today');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [paymentTypeFilter, setPaymentTypeFilter] = useState<PaymentTypeFilter>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [exporting, setExporting] = useState(false);

  // Calculate date range based on selection
  const getDateRange = (): { start: Date; end: Date } => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

    switch (dateRange) {
      case 'today':
        return { start: today, end: endOfDay };
      case 'last7days':
        const last7 = new Date(today);
        last7.setDate(last7.getDate() - 7);
        return { start: last7, end: endOfDay };
      case 'last30days':
        const last30 = new Date(today);
        last30.setDate(last30.getDate() - 30);
        return { start: last30, end: endOfDay };
      case 'thisMonth':
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        return { start: startOfMonth, end: endOfDay };
      case 'lastMonth':
        const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
        return { start: startOfLastMonth, end: endOfLastMonth };
      case 'allTime':
        return { start: new Date(2020, 0, 1), end: endOfDay };
      case 'custom':
        return {
          start: customStartDate ? new Date(customStartDate) : new Date(2020, 0, 1),
          end: customEndDate ? new Date(customEndDate + 'T23:59:59.999') : endOfDay,
        };
      default:
        return { start: today, end: endOfDay };
    }
  };

  // Filter and prepare report data
  const reportData = useMemo(() => {
    const { start, end } = getDateRange();

    // Filter parent links only (exclude split children for summary view)
    const parentLinks = paymentLinks.filter(link => !link.splitParentId);

    return parentLinks.filter((link) => {
      // Only show links that have actual payments (not just visits)
      // Reports are used for payment tracking, not visit tracking
      if (!link.payments || link.payments.length === 0) {
        return false;
      }

      // Date filter - check if any payment falls within range
      const hasPaymentInRange = link.payments.some((payment) => {
        const paymentDate = payment.timestamp.toDate();
        return paymentDate >= start && paymentDate <= end;
      });

      if (!hasPaymentInRange) return false;

      // Status filter
      if (statusFilter !== 'all') {
        if (statusFilter === 'success' && link.status !== 'success') return false;
        if (statusFilter === 'partial' && link.status !== 'partial') return false;
        if (statusFilter === 'pending' && !['pending', 'visited'].includes(link.status)) return false;
      }

      // Payment type filter
      if (paymentTypeFilter !== 'all' && link.payments?.length > 0) {
        const hasOnline = link.payments.some(p => !p.isManual);
        const hasManual = link.payments.some(p => p.isManual);
        if (paymentTypeFilter === 'online' && !hasOnline) return false;
        if (paymentTypeFilter === 'manual' && !hasManual) return false;
      }

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesName = link.customerName.toLowerCase().includes(query);
        const matchesInvoice = link.invoiceNumber?.toLowerCase().includes(query);
        const matchesEmail = link.customerEmail.toLowerCase().includes(query);
        if (!matchesName && !matchesInvoice && !matchesEmail) return false;
      }

      return true;
    }).sort((a, b) => {
      // Sort by most recent payment first
      const getLatestPaymentDate = (link: PaymentLink) => {
        if (!link.payments || link.payments.length === 0) return 0;
        const successfulPayments = link.payments.filter(p => p.status === 'success');
        if (successfulPayments.length === 0) return 0;
        return Math.max(...successfulPayments.map(p => p.timestamp.toDate().getTime()));
      };
      
      return getLatestPaymentDate(b) - getLatestPaymentDate(a);
    });
  }, [paymentLinks, dateRange, customStartDate, customEndDate, statusFilter, paymentTypeFilter, searchQuery]);

  // Format payments for display
  const formatPayments = (payments: Payment[]): string => {
    if (!payments || payments.length === 0) return '-';
    const successfulPayments = payments.filter(p => p.status === 'success');
    if (successfulPayments.length === 0) return '-';

    const amounts = successfulPayments.map(p => formatCurrency(p.amount, p.currency));
    const total = successfulPayments.reduce((sum, p) => sum + p.amount, 0);
    const currency = successfulPayments[0]?.currency || 'EUR';

    if (successfulPayments.length === 1) {
      return amounts[0];
    }
    return `${amounts.join(' + ')} = ${formatCurrency(total, currency)}`;
  };

  // Format payment dates
  const formatPaymentDates = (payments: Payment[]): string => {
    if (!payments || payments.length === 0) return '-';
    const successfulPayments = payments.filter(p => p.status === 'success');
    if (successfulPayments.length === 0) return '-';

    return successfulPayments
      .map(p => formatDateTime(p.timestamp.toDate()))
      .join(', ');
  };

  // Get payment method description
  const getPaymentMethod = (payments: Payment[]): string => {
    if (!payments || payments.length === 0) return '-';
    const successfulPayments = payments.filter(p => p.status === 'success');
    if (successfulPayments.length === 0) return '-';

    const methods = successfulPayments.map(p => {
      if (p.isManual) {
        const methodNames: Record<string, string> = {
          cash: 'Cash',
          bank_transfer: 'Bank Transfer',
          wire_transfer: 'Wire Transfer',
          check: 'Check',
          other: 'Other',
        };
        return `Manual (${methodNames[p.paymentMethod || 'other'] || 'Other'})`;
      } else {
        return `Online (Card ***${p.cardLastFour || 'XXXX'})`;
      }
    });

    return [...new Set(methods)].join(', ');
  };

  // Get status display
  const getStatusChip = (status: string, totalPaid: number, remainingAmount: number): React.ReactNode => {
    let displayStatus = status;
    let color: 'success' | 'warning' | 'default' = 'default';

    if (totalPaid === 0) {
      displayStatus = 'Pending';
      color = 'default';
    } else if (remainingAmount > 0) {
      displayStatus = 'Partial';
      color = 'warning';
    } else {
      displayStatus = 'Completed';
      color = 'success';
    }

    return <Chip label={displayStatus} size="small" color={color} />;
  };

  // Export to Excel
  const handleExport = async () => {
    setExporting(true);
    try {
      const { start, end } = getDateRange();
      const exportData = reportData.map(link => ({
        'Invoice Number': link.invoiceNumber || 'N/A',
        'Customer Name': link.customerName,
        'Customer Email': link.customerEmail,
        'Invoice Amount': link.invoiceAmount,
        'Total Paid': link.totalPaid,
        'Remaining': link.remainingAmount,
        'Currency': link.currency,
        'Payment Amount(s)': formatPayments(link.payments),
        'Payment Date(s)': formatPaymentDates(link.payments),
        'Status': link.totalPaid === 0 ? 'Pending' : (link.remainingAmount > 0 ? 'Partial' : 'Completed'),
        'Payment Method': getPaymentMethod(link.payments),
        'Created': formatDateTime(link.createdAt.toDate()),
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Payment Report');

      // Auto-size columns
      const colWidths = Object.keys(exportData[0] || {}).map(key => ({
        wch: Math.max(key.length, 15),
      }));
      worksheet['!cols'] = colWidths;

      // Generate filename with date range
      const startStr = start.toISOString().split('T')[0];
      const endStr = end.toISOString().split('T')[0];
      const filename = `Payment_Report_${startStr}_to_${endStr}.xlsx`;

      // Export
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, filename);
    } catch (error) {
      console.error('Error exporting report:', error);
    } finally {
      setExporting(false);
    }
  };

  // Calculate totals
  const totals = useMemo(() => {
    return reportData.reduce(
      (acc, link) => ({
        invoiceTotal: acc.invoiceTotal + link.invoiceAmount,
        paidTotal: acc.paidTotal + link.totalPaid,
        remainingTotal: acc.remainingTotal + link.remainingAmount,
      }),
      { invoiceTotal: 0, paidTotal: 0, remainingTotal: 0 }
    );
  }, [reportData]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, height: '90vh' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Assessment />
        <Typography variant="h6" component="span">
          Payment Reports
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* Date Range Selection */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Date Range</InputLabel>
              <Select
                value={dateRange}
                label="Date Range"
                onChange={(e) => setDateRange(e.target.value as DateRangeOption)}
              >
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="last7days">Last 7 Days</MenuItem>
                <MenuItem value="last30days">Last 30 Days</MenuItem>
                <MenuItem value="thisMonth">This Month</MenuItem>
                <MenuItem value="lastMonth">Last Month</MenuItem>
                <MenuItem value="allTime">All Time</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Custom Date Range */}
          {dateRange === 'custom' && (
            <>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Start Date"
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="End Date"
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </>
          )}

          {/* Status Filter */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="success">Completed</MenuItem>
                <MenuItem value="partial">Partial</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Payment Type Filter */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Payment Type</InputLabel>
              <Select
                value={paymentTypeFilter}
                label="Payment Type"
                onChange={(e) => setPaymentTypeFilter(e.target.value as PaymentTypeFilter)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="online">Online Only</MenuItem>
                <MenuItem value="manual">Manual Only</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Search */}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              placeholder="Name, invoice, or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <FilterList sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
        </Grid>

        {/* Summary */}
        <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Stack direction="row" spacing={4} flexWrap="wrap">
            <Box>
              <Typography variant="caption" color="text.secondary">Total Records</Typography>
              <Typography variant="h6">{reportData.length}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Invoice Total</Typography>
              <Typography variant="h6" color="primary">{formatCurrency(totals.invoiceTotal, 'EUR')}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Paid Total</Typography>
              <Typography variant="h6" color="success.main">{formatCurrency(totals.paidTotal, 'EUR')}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Remaining Total</Typography>
              <Typography variant="h6" color="warning.main">{formatCurrency(totals.remainingTotal, 'EUR')}</Typography>
            </Box>
          </Stack>
        </Box>

        {/* Report Table */}
        {reportData.length === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            No records found for the selected criteria.
          </Alert>
        ) : (
          <TableContainer component={Paper} sx={{ maxHeight: 'calc(90vh - 400px)' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Invoice #</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell align="right">Payment Amount(s)</TableCell>
                  <TableCell>Payment Date(s)</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Payment Method</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.map((link) => (
                  <TableRow key={link.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {link.invoiceNumber || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {link.customerName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {link.customerEmail}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {formatPayments(link.payments)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                        {formatPaymentDates(link.payments)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {getStatusChip(link.status, link.totalPaid, link.remainingAmount)}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                        {getPaymentMethod(link.payments)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} startIcon={<Close />}>
          Close
        </Button>
        <Button
          variant="contained"
          color="success"
          startIcon={exporting ? <CircularProgress size={20} color="inherit" /> : <FileDownload />}
          onClick={handleExport}
          disabled={exporting || reportData.length === 0}
        >
          {exporting ? 'Exporting...' : 'Export to Excel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReportsDialog;
